<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0">Ventas</h6>
              <button
                type="button"
                class="btn btn-primary mb-0"
                :disabled="generatingDispatch || selectedIds.length === 0"
                @click="generateDispatchReport"
              >
                <i :class="generatingDispatch ? 'fas fa-spinner fa-spin me-1' : 'fas fa-file-pdf me-1'"></i>
                Generar relación de despacho
                <span v-if="selectedIds.length" class="badge bg-light text-dark ms-1">{{ selectedIds.length }}</span>
              </button>
            </div>

            <div class="row">
              <!-- Columna de búsqueda -->
              <div class="col-md-3 mb-3">
                <label for="searchQuery" class="mb-1">Buscar</label>
                <input type="text" v-model="searchQuery" id="searchQuery" class="form-control"
                  placeholder="Buscar remisión..." @input="applyFilters" />
              </div>
              <!-- Filtro por estado de pedido -->
              <div class="col-md-3 mb-3">
                <label for="orderStatusFilter" class="mb-1">Estado de pedido</label>
                <select id="orderStatusFilter" v-model="orderStatusFilter" class="form-select" @change="applyFilters">
                  <option value="">Todos</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="aceptado">Aceptado</option>
                  <option value="facturado">Facturado</option>
                  <option value="enviado">Enviado</option>
                  <option value="recibido">Recibido</option>
                  <option value="devolucion">Devolución</option>
                </select>
              </div>
              <!-- Fecha inicio -->
              <div class="col-md-2 mb-3">
                <label for="dateFrom" class="mb-1">Fecha inicio</label>
                <input type="date" id="dateFrom" v-model="dateFrom" class="form-control" @change="applyFilters" />
              </div>
              <!-- Fecha fin -->
              <div class="col-md-2 mb-3">
                <label for="dateTo" class="mb-1">Fecha fin</label>
                <input type="date" id="dateTo" v-model="dateTo" class="form-control" @change="applyFilters" />
              </div>
              <!-- Limpiar filtros -->
              <div class="col-md-2 mb-3 d-flex align-items-end">
                <button type="button" class="btn btn-outline-secondary w-100 mb-0" @click="clearFilters">
                  <i class="fas fa-eraser me-1"></i> Limpiar filtros
                </button>
              </div>
            </div>

          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      <input
                        type="checkbox"
                        :checked="allSelected"
                        :indeterminate.prop="someSelected"
                        @change="toggleSelectAll($event.target.checked)"
                        title="Seleccionar todas las órdenes visibles"
                      />
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      N°</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Cliente</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Vendedor</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Teléfono</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Estado de pedido</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Total venta</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Total pagado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Siigo</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="value in data" :key="value.id">
                    <td class="align-middle text-center">
                      <input
                        type="checkbox"
                        :value="value.id"
                        v-model="selectedIds"
                      />
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ value.id }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center w-100">
                          <h6 class="mb-0 text-sm">{{ value.name }}</h6>
                          <span
                            v-if="value.observation"
                            class="text-xs text-secondary cursor-pointer observation-cell"
                            :title="value.observation"
                            @click="viewObservation(value.observation)"
                          >
                            {{ value.observation }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ sellerName(value) }}</h6>
                        </div>
                      </div>
                    </td>
                    <td class="align-middle text-center">
                      <h6 class="mb-0 text-sm">{{ value.phone }}</h6>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" :class="remissionStatusClass(value.status)">
                        {{ remissionStatusLabel(value.status) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <div class="d-flex flex-column align-items-center gap-1">
                        <span class="badge badge-sm" :class="orderStatusClass(value.order_status)">
                          {{ orderStatusLabel(value.order_status) }}
                        </span>
                        <span v-if="value.invoice_number" class="text-xs text-secondary">
                          Fact: {{ value.invoice_number }}
                        </span>
                        <button
                          v-if="nextOrderStatus(value.order_status)"
                          type="button"
                          class="btn btn-sm btn-outline-primary py-1 px-2 mb-0"
                          @click="advanceOrderStatus(value)"
                        >
                          <i class="fas fa-arrow-right me-1"></i>
                          {{ orderStatusLabel(nextOrderStatus(value.order_status)) }}
                        </button>
                      </div>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{ formatMoney(saleTotal(value)) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="text-xs font-weight-bold"
                        :class="isPartiallyPaid(value) ? 'text-danger' : 'text-secondary'"
                      >
                        {{ formatMoney(totalPaid(value)) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{ new Date(value.created_at).toLocaleDateString('es-ES') }}
                      </span>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span
                        v-if="value.siigo_id"
                        class="badge badge-sm bg-gradient-info"
                        :title="value.siigo_id"
                      >
                        Sincronizado
                      </span>
                      <button
                        v-else-if="value.can_sync_siigo"
                        type="button"
                        class="btn btn-sm btn-outline-info py-1 px-2 mb-0"
                        :disabled="syncingId === value.id"
                        @click="syncSiigo(value)"
                      >
                        <i
                          :class="syncingId === value.id ? 'fas fa-sync fa-spin me-1' : 'fas fa-cloud-upload-alt me-1'"
                        ></i>
                        Sincronizar
                      </button>
                      <span v-else class="text-muted text-xs">—</span>
                    </td>
                    <td class="align-middle text-center">
                      <a
                        @click="openPicking(value)"
                        class="text-secondary font-weight-bold text-xs cursor-pointer"
                        data-toggle="tooltip"
                        data-original-title="Picking / Packing"
                      >
                        <i class="fas fa-dolly"></i>
                      </a>

                      <a
                        @click="printRemission(value.id)"
                        class="text-secondary font-weight-bold text-xs cursor-pointer"
                        style="margin-left: 15px;"
                        data-toggle="tooltip"
                        data-original-title="Imprimir ticket"
                      >
                        <i class="fas fa-ticket-alt"></i>
                      </a>

                      <a
                        @click="viewRemission(value)"
                        class="text-secondary font-weight-bold text-xs cursor-pointer"
                        style="margin-left: 15px;"
                        data-toggle="tooltip"
                        data-original-title="Ver y editar pedido"
                      >
                        <i class="fas fa-eye"></i>
                      </a>

                      <a
                        v-if="normalizeRemissionStatus(value.status) !== 'aceptado'"
                        @click="deleteUser(value.id)"
                        class="text-secondary font-weight-bold text-xs cursor-pointer"
                        style="margin-left: 15px;"
                        data-toggle="tooltip"
                        data-original-title="Eliminar registro"
                      >
                        <i class="fa fa-trash"></i>
                      </a>
                    </td>
                  </tr>

                  <tr v-if="data.length === 0">
                    <td colspan="13" class="text-center text-xxs">No hay registros disponibles</td>
                  </tr>
                </tbody>
              </table>

              <!-- Paginación -->
              <div class="pagination p-3">
                <a v-for="link in links" :key="link.label" class="page-item cursor-pointer" @click="fetchData(link.url)"
                  :disabled="!link.url || link.active" :class="{ 'active': link.label === currentPage.toString() }">
                  {{ link.label }}
                </a>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Swal from 'sweetalert2';
import remissionsService from '@/services/remissions.service';
import { openPosTicketWindow } from '@/utils/posTicketPrint';

export default {
  name: 'ProjectsList',
  data() {
    const query = this.$route.query || {};
    return {
      searchQuery: query.search || '',
      orderStatusFilter: query.order_status || '',
      dateFrom: query.date_from || '',
      dateTo: query.date_to || '',
      syncingId: null,
      selectedIds: [],
      generatingDispatch: false,
    };
  },
  computed: {
    ...mapGetters('remissions', ['allData', 'currentPage', 'lastPage', 'pagination', 'links']),
    currentPage() {
      return this.$store.state.remissions.currentPage;
    },
    lastPage() {
      return this.$store.state.remissions.lastPage;
    },
    data() {
      return this.allData;
    },
    links() {
      return this.$store.getters['remissions/links'];
    },
    visibleIds() {
      return this.data.map((r) => r.id);
    },
    allSelected() {
      return this.visibleIds.length > 0 && this.visibleIds.every((id) => this.selectedIds.includes(id));
    },
    someSelected() {
      return !this.allSelected && this.visibleIds.some((id) => this.selectedIds.includes(id));
    },
  },
  methods: {
    sellerName(row) {
      if (row?.seller_name) {
        return row.seller_name;
      }
      if (row?.seller?.name) {
        return row.seller.name;
      }
      return '—';
    },
    normalizeRemissionStatus(status) {
      if (status === 0 || status === '0') {
        return 'pendiente';
      }
      if (status === 1 || status === '1') {
        return 'aceptado';
      }
      if (status === 'pendiente' || status === 'aceptado' || status === 'cancelado' || status === 'devolucion') {
        return status;
      }
      return 'aceptado';
    },
    remissionStatusLabel(status) {
      const normalized = this.normalizeRemissionStatus(status);
      if (normalized === 'aceptado') return 'Aceptado';
      if (normalized === 'cancelado') return 'Cancelado';
      if (normalized === 'devolucion') return 'Devolución';
      return 'Pendiente';
    },
    remissionStatusClass(status) {
      const normalized = this.normalizeRemissionStatus(status);
      if (normalized === 'aceptado') return 'bg-success';
      if (normalized === 'cancelado') return 'bg-danger';
      if (normalized === 'devolucion') return 'bg-danger';
      return 'bg-warning';
    },
    async fetchData(pageUrl = null) {
      let page;
      if (pageUrl) {
        try {
          page = new URL(pageUrl, window.location.origin).searchParams.get('page') || 1;
        } catch (e) {
          page = 1;
        }
      } else {
        // Tras acciones (aceptar/eliminar/sincronizar) mantenemos la página actual
        page = this.currentPage;
      }
      await this.fetchDataWithPage(page);
    },
    async fetchDataWithPage(page) {
      const params = {
        search: this.searchQuery,
        order_status: this.orderStatusFilter,
        date_from: this.dateFrom,
        date_to: this.dateTo,
        page,
      };
      await this.$store.dispatch('remissions/fetchData', params);
      this.syncRouteQuery();
    },
    applyFilters() {
      // Al cambiar búsqueda, filtro o fechas, volvemos a página 1 y refrescamos
      this.fetchDataWithPage(1);
    },
    clearFilters() {
      this.searchQuery = '';
      this.orderStatusFilter = '';
      this.dateFrom = '';
      this.dateTo = '';
      this.fetchDataWithPage(1);
    },
    toggleSelectAll(checked) {
      if (checked) {
        const set = new Set(this.selectedIds);
        this.visibleIds.forEach((id) => set.add(id));
        this.selectedIds = Array.from(set);
      } else {
        const visible = new Set(this.visibleIds);
        this.selectedIds = this.selectedIds.filter((id) => !visible.has(id));
      }
    },
    async generateDispatchReport() {
      if (this.selectedIds.length === 0 || this.generatingDispatch) {
        return;
      }
      this.generatingDispatch = true;
      try {
        const response = await remissionsService.dispatchReport(this.selectedIds);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) {
          // Si el navegador bloquea el popup, forzamos descarga
          const a = document.createElement('a');
          a.href = url;
          a.download = `relacion-despacho-${Date.now()}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        console.error('Error al generar relación de despacho:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.message || 'No se pudo generar la relación de despacho.',
        });
      } finally {
        this.generatingDispatch = false;
      }
    },
    syncRouteQuery() {
      const query = {};
      if (this.searchQuery) query.search = this.searchQuery;
      if (this.orderStatusFilter) query.order_status = this.orderStatusFilter;
      if (this.dateFrom) query.date_from = this.dateFrom;
      if (this.dateTo) query.date_to = this.dateTo;
      if (this.currentPage > 1) query.page = String(this.currentPage);
      const current = this.$route.query || {};
      const same = Object.keys(query).length === Object.keys(current).length
        && Object.keys(query).every((k) => String(current[k]) === String(query[k]));
      if (!same) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
    orderStatuses() {
      return ['pendiente', 'aceptado', 'facturado', 'enviado', 'recibido', 'devolucion'];
    },
    orderStatusLabel(status) {
      const s = status || 'pendiente';
      const labels = {
        pendiente: 'Pendiente',
        aceptado: 'Aceptado',
        facturado: 'Facturado',
        enviado: 'Enviado',
        recibido: 'Recibido',
        devolucion: 'Devolución',
      };
      return labels[s] || 'Pendiente';
    },
    orderStatusClass(status) {
      const s = status || 'pendiente';
      const classes = {
        pendiente: 'bg-warning',
        aceptado: 'bg-info',
        facturado: 'bg-primary',
        enviado: 'bg-success',
        recibido: 'bg-dark',
        devolucion: 'bg-danger',
      };
      return classes[s] || 'bg-warning';
    },
    nextOrderStatus(status) {
      const s = status || 'pendiente';
      // El flujo de pedido termina en 'recibido'; no se avanza a 'devolucion'
      if (s === 'recibido' || s === 'devolucion') {
        return null;
      }
      const statuses = this.orderStatuses();
      const idx = statuses.indexOf(s);
      if (idx === -1 || idx >= statuses.length - 1) {
        return null;
      }
      return statuses[idx + 1];
    },
    async advanceOrderStatus(row) {
      const next = this.nextOrderStatus(row.order_status);
      if (!next) {
        return;
      }

      const payload = { order_status: next };

      if (next === 'facturado') {
        const result = await Swal.fire({
          title: 'Número de factura',
          text: 'Ingrese el número de factura para cambiar el estado a Facturado.',
          input: 'text',
          inputPlaceholder: 'Ej: FAC-001',
          showCancelButton: true,
          confirmButtonText: 'Confirmar',
          cancelButtonText: 'Cancelar',
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return 'El número de factura es obligatorio';
            }
            return null;
          },
        });

        if (!result.isConfirmed) {
          return;
        }

        payload.invoice_number = result.value.trim();
      }

      try {
        const response = await remissionsService.updateData(row.id, payload);
        if (response && response.status >= 200 && response.status < 300) {
          await this.fetchData();
        }
      } catch (error) {
        console.error('Error al actualizar estado de pedido:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.message || 'Error al actualizar el estado de pedido',
        });
        await this.fetchData();
      }
    },
    viewObservation(observation) {
      // Mostramos el mensaje en un SweetAlert
      Swal.fire({
        title: 'Observación',
        text: observation || 'No hay observaciones disponibles.',
        icon: 'info',  // Puedes cambiar el icono a info, success, warning, etc.
        confirmButtonText: 'Aceptar'
      });
    },
    saleTotal(row) {
      return parseFloat(row?.total ?? 0) || 0;
    },
    totalPaid(row) {
      return parseFloat(row?.total_paid ?? 0) || 0;
    },
    isPartiallyPaid(row) {
      return this.totalPaid(row) < this.saleTotal(row);
    },
    formatMoney(value) {
      return (Number(value) || 0).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async deleteUser(userId) {
      try {
        const result = await Swal.fire({
          title: '¿Estás seguro?',
          text: 'Esta acción no se puede deshacer.',
          icon: 'warning',
          showCancelButton: false,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Sí, eliminar!',
          cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
          const response = await this.$store.dispatch("remissions/delete", userId);
          if (response && response.status >= 200 && response.status < 300) {
            this.fetchData();
            await Swal.fire(
              'Eliminado!',
              'El registro se ha eliminado correctamente.',
              'success'
            );
          } else {
            throw new Error(response.data.message || 'Error al eliminar el registro');
          }
        }
      } catch (error) {
        console.error("Error al eliminar el registro:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Error al eliminar el registro',
        });
      }
    },
    printRemission(id) {
      openPosTicketWindow(this.$router, id);
    },
    openPicking(remission) {
      if (!remission?.id) {
        return;
      }
      this.$router.push({
        name: 'Remissions Picking',
        query: { id: String(remission.id) },
      });
    },
    viewRemission(remission) {
      if (!remission?.id) {
        return;
      }
      this.$router.push({
        name: 'Remissions Create',
        query: { id: String(remission.id) },
      });
    },
    async syncSiigo(remission) {
      if (!remission?.id || this.syncingId) {
        return;
      }

      const confirm = await Swal.fire({
        title: 'Sincronizar con Siigo',
        html: `¿Enviar la venta <strong>#${remission.id}</strong> a Siigo?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sincronizar',
        cancelButtonText: 'Cancelar',
      });

      if (!confirm.isConfirmed) {
        return;
      }

      this.syncingId = remission.id;

      try {
        const response = await remissionsService.syncSiigo(remission.id);
        await Swal.fire({
          icon: 'success',
          title: 'Sincronizado',
          text: response.data?.message || 'Venta enviada a Siigo correctamente.',
        });
        await this.fetchData();
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          'No se pudo sincronizar la venta con Siigo.';
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg,
        });
      } finally {
        this.syncingId = null;
      }
    },
  },
  mounted() {
    const initialPage = parseInt(this.$route.query.page, 10) || 1;
    this.fetchDataWithPage(initialPage);
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
}

.active {
  font-weight: bold;
}

.observation-cell {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
